import { useState, useEffect, useMemo, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, Loader2 } from "lucide-react";
import AuditLogDetailModal from "@/components/admin/AuditLogDetailModal";
import AdminSearchPanel from "@/components/admin/AdminSearchPanel";

const ACTION_COLORS = {
  patient_login: "bg-blue-50 text-blue-700", employee_login: "bg-purple-50 text-purple-700",
  patient_register: "bg-emerald-50 text-emerald-700", logout: "bg-slate-100 text-slate-600 dark:text-slate-300",
  order_approved: "bg-emerald-50 text-emerald-700", order_denied: "bg-red-50 text-red-700",
  document_upload: "bg-amber-50 text-amber-700", create_employee: "bg-purple-50 text-purple-700",
  view_patient_record: "bg-blue-50 text-blue-700",
  client_error: "bg-rose-100 text-rose-700 ring-1 ring-rose-300",
};

const LIMIT = 50;
const EMPTY = { q: "", action: "", days: "" };

export default function AuditLogsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(EMPTY);
  const [detailLogId, setDetailLogId] = useState(null);

  useEffect(() => {
    API.get("/audit-logs/actions").then(({ data }) => {
      // Defensive: some legacy audit rows have non-string action values.
      // Filter to strings so the <Select> options don't crash on .replace().
      const safe = Array.isArray(data) ? data.filter((a) => typeof a === "string" && a.trim()) : [];
      setActions(safe);
    }).catch(() => {});
  }, []);

  const filterDefs = useMemo(() => ([
    {
      key: "action", label: "Action", type: "select",
      options: actions.map((a) => ({ value: a, label: a.replace(/_/g, " ") })),
    },
    { key: "days", label: "Time range", type: "days" },
  ]), [actions]);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams({ limit: String(LIMIT), skip: String(page * LIMIT) });
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) params.set(k, v); });
    return params.toString();
  }, [filters, page]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/audit-logs?${buildQuery()}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) { console.error("Load error:", e); } finally { setLoading(false); }
  }, [buildQuery]);

  // Reset to first page whenever filters change.
  useEffect(() => { setPage(0); }, [filters.q, filters.action, filters.days]);
  useEffect(() => {
    const t = setTimeout(loadLogs, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadLogs, filters.q]);

  return (
    <div data-testid="audit-logs-tab">
      <AdminSearchPanel
        scope="audit"
        value={filters}
        onChange={(v) => setFilters({ ...EMPTY, ...v })}
        filters={filterDefs}
        searchPlaceholder="Search by user, action, resource, IP, or details…"
        total={total}
        resultCount={logs.length}
      />

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500 dark:text-slate-400">{total.toLocaleString()} total events</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-lg" onClick={() => {
            const params = new URLSearchParams();
            if (filters.action) params.set("action", filters.action);
            window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/audit-csv?${params}`, "_blank");
          }} data-testid="export-audit-csv"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button variant="outline" size="sm" className="rounded-lg" onClick={loadLogs} data-testid="audit-refresh">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Refresh
          </Button>
        </div>
      </div>

      {loading && logs.length === 0 ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading audit logs...</div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto overflow-x-auto" data-testid="audit-scroll-container">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-600 dark:text-slate-300 font-medium">Timestamp</th>
                  <th className="text-left px-4 py-3 text-slate-600 dark:text-slate-300 font-medium">Action</th>
                  <th className="text-left px-4 py-3 text-slate-600 dark:text-slate-300 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-slate-600 dark:text-slate-300 font-medium hidden md:table-cell">Role</th>
                  <th className="text-left px-4 py-3 text-slate-600 dark:text-slate-300 font-medium hidden lg:table-cell">Details</th>
                  <th className="text-left px-4 py-3 text-slate-600 dark:text-slate-300 font-medium hidden lg:table-cell">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {logs.map((log) => {
                  const actionKey = typeof log.action === "string" ? log.action : "unknown";
                  return (
                  <tr key={log.id} className={`cursor-pointer ${actionKey === "client_error" ? "bg-rose-50/40 hover:bg-rose-50" : "hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40"}`} onClick={() => setDetailLogId(log.id)} data-testid={`audit-row-${log.id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${ACTION_COLORS[actionKey] || "bg-slate-100 text-slate-600 dark:text-slate-300"}`}>
                        {actionKey === "client_error" && (
                          <span className="relative flex h-2 w-2" data-testid="severity-pulse">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                          </span>
                        )}
                        {actionKey.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-900 dark:text-white">{typeof log.user_name === "string" ? log.user_name : (log.user_name ? JSON.stringify(log.user_name) : "-")}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 hidden md:table-cell capitalize">{typeof log.role === "string" ? log.role : ""}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 hidden lg:table-cell max-w-[300px] truncate">{typeof log.details === "string" ? log.details : ""}</td>
                    <td className="px-4 py-2 text-xs text-slate-400 dark:text-slate-500 font-mono hidden lg:table-cell">{typeof log.ip_address === "string" ? log.ip_address : (typeof log.ip === "string" ? log.ip : "")}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {logs.length === 0 && <div className="p-8 text-center text-slate-400 dark:text-slate-500">No audit logs found</div>}
          </div>
        </div>
      )}

      <AuditLogDetailModal logId={detailLogId} open={!!detailLogId} onClose={() => setDetailLogId(null)} />

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-slate-500 dark:text-slate-400">Page {page + 1} of {Math.ceil(total / LIMIT)}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev">Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
